import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, User, Tag } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/blog/${slug}`);
        setPost(res.data);
      } catch (error) {
        console.error("Error fetching blog post:", error);
        setError("Blog post not found");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24" data-testid="blog-post-loading">
        <div className="py-24 md:py-32">
          <div className="max-w-3xl mx-auto px-6">
            <Skeleton className="h-6 w-32 bg-white/10 mb-8" />
            <Skeleton className="h-16 w-full bg-white/10 mb-6" />
            <Skeleton className="h-8 w-2/3 bg-white/10 mb-12" />
            <div className="space-y-4">
              <Skeleton className="h-4 w-full bg-white/10" />
              <Skeleton className="h-4 w-full bg-white/10" />
              <Skeleton className="h-4 w-3/4 bg-white/10" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen pt-24" data-testid="blog-post-error">
        <div className="py-24 md:py-32 text-center">
          <div className="max-w-3xl mx-auto px-6">
            <h1 className="text-4xl font-heading font-bold text-white mb-6">Post Not Found</h1>
            <p className="text-slate-400 text-lg mb-10">The blog post you're looking for doesn't exist.</p>
            <Link 
              to="/blog"
              className="inline-flex items-center gap-2 text-mint hover:text-coral transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24" data-testid="blog-post-page">
      <article className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Back Link */}
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-slate-500 hover:text-electric transition-colors mb-12"
            data-testid="blog-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-xs font-mono uppercase tracking-wider">Back to Blog</span>
          </Link>

          {/* Header */}
          <header className="mb-16">
            <span className="text-xs font-mono uppercase tracking-wider px-3 py-1 bg-mint/10 text-mint border border-mint/20">
              {post.category}
            </span>
            <h1 className="mt-8 text-4xl sm:text-5xl font-heading font-bold text-white tracking-tight leading-[1.1]">
              {post.title}
            </h1>
            <p className="mt-8 text-xl text-slate-300 leading-relaxed">
              {post.excerpt}
            </p>
            
            <div className="mt-10 flex flex-wrap items-center gap-8 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(post.created_at)}</span>
              </div>
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="mt-8 flex flex-wrap items-center gap-2">
                <Tag className="w-4 h-4 text-slate-600" />
                {post.tags.map((tag) => (
                  <span 
                    key={tag}
                    className="px-3 py-1 bg-white/5 text-slate-400 text-xs border border-white/5"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </header>

          {/* Content */}
          <div className="prose-raindas" data-testid="blog-post-content">
            {post.content.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="text-slate-300 text-lg leading-relaxed mb-8">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Footer */}
          <footer className="mt-20 pt-10 border-t border-white/5">
            <Link 
              to="/blog"
              className="inline-flex items-center gap-2 text-white hover:text-mint transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              View All Articles
            </Link>
          </footer>
        </div>
      </article>
    </div>
  );
}
